import React, { useState, useEffect } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Button } from "flowbite-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "../../../../toast";
import AdminLayout from "../../Layout/index.jsx";
import { auth } from "../../../../utils/firebase";
import BulkCreateStudentsStep, { ACTION_MODES } from "./BulkCreateStudentsStep.jsx";
import ClusterStep from "./ClusterStep.jsx";
import ReviewStep from "./ReviewStep.jsx";
import UploadResponseModal from "./UploadResponseModal.jsx";

import {
  useBulkUploadStudentsMutation,
  useGetCampusQuery,
} from "../../../../graphql/generated.tsx";

const superAdminEmails = [
  "training@thebettertomorrow.in",
  "bettertomorrowoffi@gmail.com",
  "beta-admin@bt.in",
];

// ✅ Enable test mode for creating fake errors
const ENABLE_TEST_MODE = true;

const BULK_MODE_TABS = [
  { key: ACTION_MODES.create, label: "Create Student" },
  { key: ACTION_MODES.resend, label: "Resend Invite" },
  { key: ACTION_MODES.delete, label: "Bulk Delete" },
];

const CreateStudents = () => { 
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: campusDetails } = useGetCampusQuery({ variables: { id: id } });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !superAdminEmails.includes(user.email)) {
        navigate("/admin", { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [clusterid, setClusterId] = useState("");
  const [bulkupload, { loading: createLoading }] = useBulkUploadStudentsMutation();

  const [showModal, setShowModal] = useState(false);
  const [tab, setTab] = useState("success");
  const [successList, setSuccessList] = useState([]);
  const [duplicateList, setDuplicateList] = useState([]);
  const [errorList, setErrorList] = useState([]);
  const [bulkActionMode, setBulkActionMode] = useState(ACTION_MODES.create);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  useEffect(() => {
    if (currentStep !== 0) setBulkActionMode(ACTION_MODES.create);
  }, [currentStep]);

  const mergeAll = (existing, incoming) => [...existing, ...incoming];

  // ✅ Load TEST DATA automatically (only first time)
  useEffect(() => {
    if (ENABLE_TEST_MODE) {
     

      const testErrors = [
      ];

      const testSuccess = [
      ];

      const testDuplicate = [
      ];

      setSuccessList(testSuccess);
      setDuplicateList(testDuplicate);
      setErrorList(testErrors);

      toast.warn("✅ Loaded test error data for retry testing.");

      setTab("error");
    }
  }, []);

  const submitForm = async () => {
    try {
      const { data } = await bulkupload({
        variables: {
          students: formData.students,
          campusName: campusDetails?.campus?.name,
          clusterId: clusterid,
        },
      });

      const res = data?.bulkUploadStudents || [];
      const success = [], duplicate = [], errors = [];

      res.forEach((entry) => {
        const msg = entry?.message?.toLowerCase() || "";
        if (msg.includes("already exists")) duplicate.push(entry);
        else if (msg.includes("firebase user created")) success.push(entry);
        else errors.push(entry);
      });

      const stored = JSON.parse(localStorage.getItem("lastUploadResults")) || {
        success: [],
        duplicate: [],
        errors: [],
      };

      const mergedSuccess = mergeAll(stored.success, success);
      const mergedDuplicate = mergeAll(stored.duplicate, duplicate);
      const mergedErrors = mergeAll(stored.errors, errors);

      setSuccessList(mergedSuccess);
      setDuplicateList(mergedDuplicate);
      setErrorList(mergedErrors);

      localStorage.setItem(
        "lastUploadResults",
        JSON.stringify({
          success: mergedSuccess,
          duplicate: mergedDuplicate,
          errors: mergedErrors,
        })
      );

      setTab(
        success.length ? "success" :
        duplicate.length ? "duplicate" :
        "error"
      );

      setShowModal(true);

      setFormData({});
      setClusterId("");
      setCurrentStep(0);
    } catch (error) {
      setSuccessList([]);
      setDuplicateList([]);
      setErrorList([{ email: "N/A", message: error.message }]);
      setTab("error");
      setShowModal(true);
      toast.error("Upload failed.");
    }
  };

  // ✅ RETRY FAILED ENTRIES
  const retryFailedUploads = async () => {
    if (errorList.length === 0) {
      toast.info("No failed entries to retry.");
      return;
    }

    const retryStudents = errorList.map((item) => ({
      email: item.email,
      name: item.name || "Retry User",
      phone: item.phone || "0000000000",
    }));

    toast.info("Retrying failed uploads…");

    try {
      const { data } = await bulkupload({
        variables: {
          students: retryStudents,
          campusName: campusDetails?.campus?.name,
          clusterId: clusterid,
        },
      });

      const res = data?.bulkUploadStudents || [];
      const retrySuccess = [], retryDuplicate = [], retryErrors = [];

      res.forEach((entry) => {
        const msg = entry?.message?.toLowerCase() || "";
        if (msg.includes("already exists")) retryDuplicate.push(entry);
        else if (msg.includes("firebase user created")) retrySuccess.push(entry);
        else retryErrors.push(entry);
      });

      const newSuccess = [...successList, ...retrySuccess];
      const newDuplicate = [...duplicateList, ...retryDuplicate];

      setSuccessList(newSuccess);
      setDuplicateList(newDuplicate);
      setErrorList(retryErrors);

      localStorage.setItem(
        "lastUploadResults",
        JSON.stringify({
          success: newSuccess,
          duplicate: newDuplicate,
          errors: retryErrors,
        })
      );

      toast.success(
        `Retry done → ${retrySuccess.length} success, ${retryDuplicate.length} duplicates, ${retryErrors.length} failed again.`
      );

      setTab(
        retrySuccess.length ? "success" :
        retryDuplicate.length ? "duplicate" : "error"
      );

    } catch (err) {
      toast.error("Retry failed.");
    }
  };

  const viewPreviousResults = () => {
    const stored = localStorage.getItem("lastUploadResults");
    if (!stored) {
      toast.info("No stored results found.");
      return;
    }
    const parsed = JSON.parse(stored);
    setSuccessList(parsed.success || []);
    setDuplicateList(parsed.duplicate || []);
    setErrorList(parsed.errors || []);
    setTab(
      parsed.success?.length ? "success" :
      parsed.duplicate?.length ? "duplicate" :
      "error"
    );
    setShowModal(true);
  };

  const clearStoredResults = () => {
    localStorage.removeItem("lastUploadResults");
    setSuccessList([]);
    setDuplicateList([]);
    setErrorList([]);
    toast.info("Results cleared.");
  };

  /** Stepper labels match the selected bulk action (always visible on step 0). */
  const bulkStepLabels =
    bulkActionMode === ACTION_MODES.resend
      ? ["Enter emails & send invitation"]
      : bulkActionMode === ACTION_MODES.delete
        ? ["Enter emails & confirm deletion"]
        : ["Bulk Create Students", "Create/Add to Cluster", "Review"];

  /** Steps 1–2 only exist for bulk create; keep same header + stepper as step 0. */
  const showBulkHeaderAndStepper =
    currentStep === 0 || currentStep === 1 || currentStep === 2;

  const selectCreateMode = () => setBulkActionMode(ACTION_MODES.create);
  const selectResendMode = () => {
    setBulkActionMode(ACTION_MODES.resend);
    setCurrentStep(0);
  };
  const selectDeleteMode = () => {
    setBulkActionMode(ACTION_MODES.delete);
    setCurrentStep(0);
  };

  return (
    <AdminLayout>
      <div className="min-h-screen px-6 py-8 md:px-10 md:py-10">
        <div className="flex items-center justify-between gap-4 mb-8 max-md:flex-col max-md:items-stretch">

          <div className="flex items-center">
            <button
              onClick={() => window.history.back()}
              className="text-gray-500 hover:text-gray-700"
            >
              <IoMdArrowRoundBack className="w-6 h-6" />
            </button>
            <div className="ml-2 text-2xl font-bold max-md:text-lg">
              Create Students
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <Button color="light" onClick={viewPreviousResults}>
              View Upload Summary
            </Button>

            <Button color="light" onClick={clearStoredResults}>
              Clear Stored Results
            </Button>

            {errorList.length > 0 && (
              <Button color="light" onClick={retryFailedUploads}>
                Retry Failed Uploads ({errorList.length})
              </Button>
            )}
          </div>

        </div>

        <div className="flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl flex flex-col p-5 sm:p-6 h-[min(32rem,calc(100vh-9rem))] shrink-0">
            {showBulkHeaderAndStepper && (
              <div className="flex flex-col gap-4 shrink-0">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                  {BULK_MODE_TABS.map(({ key, label }) => {
                    const isActive = bulkActionMode === key;
                      const onSelect =
                        key === ACTION_MODES.create
                          ? selectCreateMode
                          : key === ACTION_MODES.resend
                            ? selectResendMode
                            : selectDeleteMode;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={onSelect}
                          className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                            isActive
                              ? "bg-primary text-white shadow-sm"
                              : "text-primary hover:bg-primary/10"
                          }`}
                        >
                          {label}
                        </button>
                    );
                  })}
                </div>

                <ol
                  className={`flex flex-wrap items-center w-full p-3 sm:p-3.5 gap-x-2 gap-y-2 sm:gap-x-4 text-xs sm:text-sm font-medium text-gray-500 bg-slate-50 border border-gray-200 rounded-lg ${
                    bulkStepLabels.length === 1
                      ? "justify-center"
                      : "justify-between"
                  }`}
                >
                  {bulkStepLabels.map((label, i) => (
                    <li
                      key={`${bulkActionMode}-${i}-${label}`}
                      className={`flex items-center shrink-0 min-w-0 ${
                        currentStep === i ? "text-sky-800" : ""
                      }`}
                    >
                      <span
                        className={`flex items-center justify-center w-6 h-6 me-2 text-[11px] sm:text-xs font-semibold border ${
                          currentStep === i
                            ? "border-sky-600 bg-sky-50 text-sky-900"
                            : "border-gray-400 text-gray-500"
                        } rounded-full`}
                      >
                        {i + 1}
                      </span>
                      {label}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div
              className={`flex flex-col flex-1 min-h-0 overflow-hidden ${showBulkHeaderAndStepper ? "mt-4" : ""}`}
            >
              <div className="flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-contain">
                {currentStep === 0 && (
                  <BulkCreateStudentsStep
                    formData={formData}
                    setFormData={setFormData}
                    nextStep={nextStep}
                    actionMode={bulkActionMode}
                    campusName={campusDetails?.campus?.name}
                  />
                )}

                {currentStep === 1 && (
                  <ClusterStep
                    formData={formData}
                    setFormData={setFormData}
                    prevStep={prevStep}
                    nextStep={nextStep}
                    campusDetails={campusDetails}
                    setClusterId={setClusterId}
                  />
                )}

                {currentStep === 2 && (
                  <ReviewStep
                    formData={formData}
                    prevStep={prevStep}
                    submitForm={submitForm}
                    createLoading={createLoading}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UploadResponseModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        tab={tab}
        setTab={setTab}
        successList={successList}
        duplicateList={duplicateList}
        errorList={errorList}
      />
    </AdminLayout>
  );
};

export default CreateStudents;
